import { Injectable, inject } from "@angular/core";
import { AlertController, ModalController } from "@ionic/angular";
import { firstValueFrom, map } from "rxjs";

import { TranslateService } from "@ngx-translate/core";
import { DeviceService } from "@services/device.service";
import { Utils } from "@shared-utils/Utils";
import { DecouplingUserServiceMSO, ErrorDecoupling } from "./DecouplingUserServiceMSO.service";
import { ErrorService } from "./ErrorService";
import { LogService } from "./LogService";
import { UserServiceMSO } from "./UserServiceMSO";
import { NewBaseService } from "./newBase.service";

import { DecouplingLoginResponse } from "@models/I_DecouplingLogin";
import { LicenseTypes } from "@models/MasterData";
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { UtilsMsoService } from "@mso-services/utilsMso.service";
import { ExtractDatePipe } from "@pipes/extractDate.pipe";
import { ParseMoney } from "@pipes/parseMoney";
import { InformationModal } from "@shared-mso-pages/information-modal/information-modal";

type ButtonsOptions = "cancel" | "accept";

const RENAPER_DATE_INIT = "2021/08/19";

@Injectable({
  providedIn: "root",
})
export class NewLoginAlertsService {
  private globalVars!: GlobalVarsModel;
  private isMobile = false;

  private alertCtr = inject(AlertController);
  private utils = inject(Utils);
  private translate = inject(TranslateService);
  private newBaseService = inject(NewBaseService);
  private extractDatePipe = inject(ExtractDatePipe);
  private decouplingService = inject(DecouplingUserServiceMSO);
  private userServiceMSO = inject(UserServiceMSO);
  private utilsMsoService = inject(UtilsMsoService);
  private parseMoney = inject(ParseMoney);
  private errorService = inject(ErrorService);
  private logService = inject(LogService);
  private modalCtrl = inject(ModalController);
  private deviceService = inject(DeviceService);

  constructor() {
    this.newBaseService.getVars.subscribe((data) => {
      this.globalVars = data;
    });
    this.isMobile = this.deviceService.isMobile();
  }

  invalidLoginForm() {
    this.utils.alert(true, "Login", this.translate.instant("checkField"), "OK", null, "", false);
  }

  showAlertPreSignup() {
    const title = "INFO";
    const text = `
            <p>Hemos detectado que usted ya tiene una cuenta online</p>
            <p>Si lo desea le enviaremos un Email/SMS
            con los sencillos pasos que debe seguir para acceder a esa cuenta</p>`;
    this.utils.alert(true, title, text, "OK");
  }

  showAlertChangePasswordTime() {
    this.utils.alert(false, "", this.translate.instant("tfailpassrenov"), "OK");
  }

  async showAlertExtendSelftExclusion() {
    const msg: string = "Ha finalizado tu periodo de Autoexclusion. <br/>¿Desea prorrogarlo?" + "<br/><br/>";
    const alert = await this.alertCtr.create({
      header: this.translate.instant("important"),
      message: msg,
      backdropDismiss: false,
      buttons: [
        {
          text: "NO",
          role: "cancel",
        },
        {
          text: "Sí, deseo prorrogralo",
          role: "accept",
        },
      ],
    });
    alert.present();
    return new Promise<ButtonsOptions>((resolve) => {
      alert.onDidDismiss().then((data) => {
        resolve((data.role as ButtonsOptions) || "cancel");
      });
    });
  }

  async showAlertWelcomeLoginMsg(loginResponse: DecouplingLoginResponse) {
    let msgBienvenida: string = this.translate.instant("m_reg_loginOk");
    const inicioRenaper = new Date(RENAPER_DATE_INIT);

    if (
      (this.globalVars.licenseType === LicenseTypes.ArgentinaCaba ||
        this.globalVars.licenseType === LicenseTypes.ArgentinaPBA) &&
      !this.globalVars.user.verificacionDocumental &&
      this.globalVars.user.registratedDate < inicioRenaper
    ) {
      const msgNoRenaper = this.translate.instant("norenaperverifdocum");
      msgBienvenida += `<p>${msgNoRenaper}</p>`;
    }

    if (loginResponse.lastLogin) {
      if (this.globalVars.licenseType === LicenseTypes.Colombia)
        msgBienvenida +=
          "<br />" +
          this.translate.instant("lastconex") +
          " " +
          this.extractDatePipe.transform(loginResponse.lastLogin, ["3"]);
      else
        msgBienvenida +=
          "<br />" +
          this.translate.instant("lastconex") +
          " " +
          this.extractDatePipe.transform(loginResponse.lastLogin);
    }

    if (loginResponse.modifDateRegulatorFeature) {
      if (loginResponse.modifDateRegulatorFeature.getTime() > loginResponse.lastLogin.getTime()) {
        try {
          msgBienvenida += await this.getRegulatorLimitsString();
        } catch (error) {
          console.error(error);
        }
      }
    }

    if (this.globalVars.licenseType === LicenseTypes.Panama && this.globalVars.isProhibited) {
      msgBienvenida += "<br>";
      msgBienvenida += this.translate.instant("txtprohibidospa");
    }

    const title: string = this.translate.instant("m_reg_regOkT");
    return this.presentAlertWelcomeLogin(title, msgBienvenida, loginResponse.alertSelfExclusion);
  }

  private async presentAlertWelcomeLogin(
    title: string,
    msgBienvenida: string,
    alertSelfExclusion: string | number = 0,
  ) {
    return new Promise<void>((resolve) => {
      this.utils.alert(
        false,
        title,
        msgBienvenida,
        this.utils.isArgentina() ? this.translate.instant("tAccept") : "OK",
        () => {
          setTimeout(async () => {
            if (this.globalVars.licenseType === LicenseTypes.ArgentinaCaba && Number(alertSelfExclusion) > 0) {
              this.utils.selfExclusionModal(alertSelfExclusion);
            } else if (
              this.globalVars.licenseType === LicenseTypes.ArgentinaPBA &&
              Number(alertSelfExclusion) === 2
            ) {
              const alert = await this.alertCtr.create({
                header: this.translate.instant("tInf"),
                message:
                  "Actualmente tu cuenta se encuentra bloqueada para realizar depósitos y apuestas por encontrarte dentro del registro de personas autoexcluidas del IPLyC.",
                backdropDismiss: false,
                cssClass: "info-autoexclusion",
                buttons: [
                  {
                    text: "OK",
                    handler: () => {
                      alert.dismiss();
                    },
                  },
                ],
              });
              alert.present();
            }
            resolve();
          }, 500);
        },
        "welcome-modal",
        true,
      );
    });
  }

  private async getRegulatorLimitsString() {
    let msgdepositlimits = "";
    if (
      this.globalVars.FEATURES.depositLimitRegulatorStartDate &&
      this.globalVars.FEATURES.depositLimitRegulatorStartDate !== ""
    ) {
      const startdate = new Date(this.globalVars.FEATURES.depositLimitRegulatorStartDate);
      startdate.setHours(0, 0, 0, 0);
      const enddate =
        this.globalVars.FEATURES.depositLimitRegulatorEndDate &&
        this.globalVars.FEATURES.depositLimitRegulatorEndDate !== ""
          ? new Date(this.globalVars.FEATURES.depositLimitRegulatorEndDate)
          : null;
      const datenow = new Date();
      if (enddate) {
        enddate.setHours(0, 0, 0, 0);
      }
      datenow.setHours(0, 0, 0, 0);
      if ((enddate && enddate.getTime() >= datenow.getTime()) || !enddate) {
        const data = await firstValueFrom(
          this.globalVars.FEATURES.MSO_DecouplingLimits
            ? this.decouplingService.decouplingGetLimits().pipe(
                map((response) => ({
                  day: `${response.depositsDay}`,
                  week: `${response.depositsWeek}`,
                  month: `${response.depositsMonth}`,
                })),
              )
            : this.userServiceMSO.getLimits(),
        );
        let intDay: number = parseInt(data.day);
        let intWeek: number = parseInt(data.week);
        let intMonth: number = parseInt(data.month);

        if (intDay === -1 || intDay === 0) intDay = parseInt(this.globalVars.FEATURES.depositDayLimit);

        if (intWeek === -1 || intWeek === 0) intWeek = parseInt(this.globalVars.FEATURES.depositWeekLimit);

        if (intMonth === -1 || intMonth === 0) intMonth = parseInt(this.globalVars.FEATURES.depositMonthLimit);
        if (
          (this.globalVars.FEATURES.depositDayLimitRegulator &&
            parseInt(this.globalVars.FEATURES.depositDayLimitRegulator) > 0) ||
          (this.globalVars.FEATURES.depositWeekLimitRegulator &&
            parseInt(this.globalVars.FEATURES.depositWeekLimitRegulator) > 0) ||
          (this.globalVars.FEATURES.depositMonthLimitRegulator &&
            parseInt(this.globalVars.FEATURES.depositMonthLimitRegulator) > 0)
        ) {
          if (
            intDay > parseInt(this.globalVars.FEATURES.depositDayLimitRegulator) ||
            intWeek > parseInt(this.globalVars.FEATURES.depositWeekLimitRegulator) ||
            intMonth > parseInt(this.globalVars.FEATURES.depositMonthLimitRegulator)
          ) {
            msgdepositlimits += `<br><br> ${this.translate.instant("limitsregulator")} ${startdate.getDate()}/${
              startdate.getMonth() + 1
            }/${startdate.getFullYear()}`;
          }
        }
      }
    }
    return msgdepositlimits;
  }

  showAlertInvalidFormCaptcha() {
    this.utils.alert(true, this.translate.instant("tRemPass"), this.translate.instant("checkField"), "OK", null);
  }

  showAlertResetPasswordOk() {
    return new Promise<void>((resolve) => {
      this.utils.showMsg(this.translate.instant("m_reg_sendPassOk"), () => {
        resolve();
      });
    });
  }

  showAlertCaptchaKO() {
    this.utils.showError(this.translate.instant("captchaFail"));
  }

  showAlertSendPassKO() {
    this.utils.showError(this.translate.instant("m_reg_sendPassKo"));
  }

  showAlertExProhibited(userName: string, balance = 0) {
    return new Promise<boolean>((resolve, reyect) => {
      this.utils.confirm(
        false,
        "",
        this.translate.instant("selfexclusionperiod"),
        "No",
        async () => {
          try {
            await firstValueFrom(this.decouplingService.reactivateAccount(userName, false));
          } catch (error) {
            console.error(error);
          }
          const alertNoAccept = await this.utils.alert(
            false,
            "",
            `${this.translate.instant("accountclosedselfbanning01")} ${this.parseMoney.transform(
              balance.toString(),
              [1],
            )} ${this.translate.instant("accountclosedselfbanning02")}`,
            "Aceptar",
            () => {
              reyect();
            },
          );

          const wrapperAlert = alertNoAccept.querySelector(".alert-wrapper");
          const link = wrapperAlert.querySelector("a");
          link.style.cursor = "pointer";
          link.addEventListener("click", ($event) => {
            $event.preventDefault();
            alertNoAccept.dismiss();
            resolve(false);
          });
        },
        "Sí",
        () => {
          //Sí acepta
          this.decouplingService.reactivateAccount(userName).subscribe({
            next: () => {
              resolve(true);
            },
            error: (err) => {
              const { type } = err as ErrorDecoupling;
              if (type && type === 190) {
                this.errorService.searchErrorCode("error" + type).subscribe({
                  next: async (datax) => {
                    const msgCode: string = datax;
                    const alert = await this.utils.alert(true, "", msgCode, "OK");

                    const wrapperAlert = alert.querySelector(".alert-wrapper");
                    const link = wrapperAlert.querySelector("a");
                    link.style.cursor = "pointer";
                    link.addEventListener("click", ($event) => {
                      $event.preventDefault();
                      alert.dismiss();
                      resolve(false);
                    });
                  },
                  error: (_) => {
                    reyect();
                  },
                });
              } else {
                reyect();
              }
            },
          });
        },
        false,
      );
    });
  }

  showAlertUserBlock() {
    this.utils.showError(this.translate.instant("alertbloquedactive"));
  }

  showAlertTillPanama() {
    return new Promise<void>((resolve) => {
      this.utils.alert(
        false,
        this.translate.instant("modalregtilltitle"),
        this.translate.instant("modalregtilltxt"),
        this.translate.instant("tAccept"),
        () => {
          resolve();
        },
      );
      ("modalregtill");
    });
  }

  showAlerErrorUpdate() {
    this.utils.showError(this.translate.instant("tfailactualiz"));
  }

  async showAlertNewTermsAndConditionsNacional(userName: string) {
    return new Promise<boolean>((resolve) => {
      let url = `${this.globalVars.DOMAIN}/csbgonline/condicionesgenerales/pdfjs/`;
      url += this.isMobile ? "mobile/index.html" : "index.html";
      this.modalCtrl
        .create({
          component: InformationModal,
          cssClass: "is-modal",
          componentProps: { url: url },
        })
        .then((modal) => {
          modal.present();
          modal.onDidDismiss().then((acceptedConditions) => {
            if (acceptedConditions?.data === true) {
              const username: string = userName.toLowerCase();
              this.logService.log("update terms:", username);
              this.utilsMsoService.updateTermsAndConditions(username).subscribe({
                next: (data2) => {
                  if (data2.status === "ok") {
                    resolve(true);
                  } else {
                    this.showAlerErrorUpdate();
                    resolve(false);
                  }
                },
                error: (_) => {
                  this.showAlerErrorUpdate();
                  resolve(false);
                },
              });
            } else {
              resolve(false);
            }
          });
        });
    });
  }

  showAlertNewTermsAndConditionsOthers(userName: string) {
    return new Promise<boolean>((resolve) => {
      const license = this.globalVars.licenseType.toString();
      const username: string = userName.toLowerCase();
      this.utilsMsoService.getLastTermsAndConditions().subscribe({
        next: async (data3) => {
          let extraUrl = null;
          if (
            this.globalVars.licenseType === LicenseTypes.Colombia ||
            this.globalVars.licenseType === LicenseTypes.ArgentinaCaba
          ) {
            extraUrl = { urlPrivacePolitics: "", urlautotratadatospersonal: "" };
            const responseprivacitypolitics = await firstValueFrom(
              this.utilsMsoService.getLastPrivacyPolitics(license),
            );

            extraUrl.urlPrivacePolitics = responseprivacitypolitics.Url;
            if (this.globalVars.licenseType === LicenseTypes.Colombia) {
              const responseautotratadatospersonal = await firstValueFrom(
                this.utilsMsoService.getLastAuthorizationPersonalData(license),
              );
              extraUrl.urlautotratadatospersonal = responseautotratadatospersonal.Url;
            }
          }
          if (this.globalVars.licenseType === LicenseTypes.ArgentinaCaba) {
            this.showAcceptTermsArg(data3.Url, extraUrl.urlPrivacePolitics, async () => {
              try {
                const request = await Promise.all([
                  firstValueFrom(this.utilsMsoService.updateTermsAndConditions(username.toLowerCase())),
                  firstValueFrom(this.utilsMsoService.updatePrivacePolitics(username.toLowerCase())),
                ]);
                if (request[0].status === "ok" && request[1].status === "ok") {
                  resolve(true);
                } else {
                  this.showAlerErrorUpdate();
                  resolve(false);
                }
              } catch (_) {
                this.showAlerErrorUpdate();
                resolve(false);
              }
            });
          } else {
            this.showAlertAcceptTerms(
              data3.Url,
              () => {
                this.logService.log("update terms:", username);
                this.utilsMsoService.updateTermsAndConditions(username).subscribe({
                  next: (data2) => {
                    if (data2.status === "ok") {
                      if (this.globalVars.licenseType === LicenseTypes.Colombia) {
                        this.utilsMsoService.updateAuthorizationPersonalData(username).subscribe({
                          next: (apdResponse) => {
                            if (apdResponse.status === "ok") {
                              this.utilsMsoService.updatePrivacePolitics(username).subscribe({
                                next: (data4) => {
                                  if (data4.status === "ok") {
                                    resolve(true);
                                  } else {
                                    this.showAlerErrorUpdate();
                                    resolve(false);
                                  }
                                },
                                error: (_) => {
                                  this.showAlerErrorUpdate();
                                  resolve(false);
                                },
                              });
                            } else {
                              this.showAlerErrorUpdate();
                              resolve(false);
                            }
                          },
                          error: (_) => {
                            this.showAlerErrorUpdate();
                            resolve(false);
                          },
                        });
                      } else {
                        resolve(true);
                      }
                    } else {
                      this.showAlerErrorUpdate();
                      resolve(false);
                    }
                  },
                  error: (_) => {
                    this.showAlerErrorUpdate();
                    resolve(false);
                  },
                });
              },
              true,
              extraUrl,
            );
          }
        },
        error: (_) => {
          this.showAlerErrorUpdate();
          resolve(false);
        },
      });
    });
  }

  private async showAcceptTermsArg(urlTyc: string, urlPrivacity: string, buttonHandler: VoidFunction) {
    const msg = `${this.translate.instant(
      "tycarg01",
    )} <a external-link id="linktyc" target="_blank" href="${urlTyc}">${this.translate.instant(
      "tycarg02",
    )}</a> ${this.translate.instant(
      "and",
    )} <a external-link id="linktyc" target="_blank" href="${urlPrivacity}">${this.translate.instant(
      "tycarg03",
    )}</a>  ${this.translate.instant("tycarg04")}`;
    const alert = await this.alertCtr.create({
      header: this.translate.instant("important"),
      cssClass: "msgtycco",
      message: msg,
      buttons: [
        {
          text: this.translate.instant("tCancel"),
          role: "cancel",
        },
        {
          text: this.translate.instant("CodereCardconfir"),
          handler: buttonHandler,
        },
      ],
    });
    await alert.present();
  }

  private async showAlertAcceptTerms(
    url: string,
    buttonHandler: VoidFunction,
    isTerms: boolean,
    extraUrl: { urlPrivacePolitics: string; urlautotratadatospersonal: string },
  ) {
    let msg: string = this.translate.instant("tfailcondiclegal") + "<br/>" + "<span><br/><br/>";

    if (this.globalVars.licenseType === LicenseTypes.Colombia) {
      msg =
        this.translate.instant("tfailcondiclegalco") +
        '&nbsp;<span><a external-link id="linkCondicionesReg" target="_blank" href="' +
        url +
        '" class="ui-link">' +
        this.translate.instant("rCond1b") +
        "</a></span>" +
        this.translate.instant("the") +
        '&nbsp;<span><a external-link id="linkpolitictrata" target="_blank" href="' +
        extraUrl.urlPrivacePolitics +
        '" class="ui-link">' +
        this.translate.instant("poltratadatospersonal") +
        "</a></span>" +
        this.translate.instant("reg_tyc2") +
        '&nbsp;<span><a external-link id="linkautodatospers" target="_blank" href="' +
        extraUrl.urlautotratadatospersonal +
        '" class="ui-link">' +
        this.translate.instant("autotratadatospersonal") +
        "</a>.</span><br/><br/>";
    }

    if (!isTerms && this.globalVars.licenseType === LicenseTypes.Colombia)
      msg = this.translate.instant("tfailprivacepol") + "<br/>" + "<span><br/><br/>";

    let msgCheckbox: string =
      "<span>" +
      this.translate.instant("reg_tyc") +
      '</span>&nbsp;<a external-link id="linkCondicionesReg" target="_blank" href="' +
      url +
      '" class="ui-link"><span>' +
      this.translate.instant("rCond1b") +
      "</span>&nbsp;<span>" +
      this.translate.instant("reg_tyc2") +
      "</span><span>" +
      this.translate.instant("rCond2b") +
      "</span></a>";
    if (this.globalVars.licenseType === LicenseTypes.Colombia) {
      const url3 = "https://www.codere.com.co/informaci%C3%B3n-legal/Notificaci%C3%B3n-Cesi%C3%B3n"; //cambiar por buena
      msgCheckbox =
        "<span>" +
        this.translate.instant("reg_tyc_co") +
        "</span><br/><br/>" +
        "<span>" +
        this.translate.instant("notcontratoconcesion") +
        "</span>&nbsp;" +
        "<span>" +
        '<a external-link id="linkcesiontratamientodatosco" target="_blank" href="' +
        url3 +
        '" class="ui-link">' +
        ">>&nbsp;" +
        this.translate.instant("seeMore") +
        "</a>";
    }
    const alert = await this.alertCtr.create({
      header:
        this.globalVars.licenseType === LicenseTypes.Colombia
          ? this.translate.instant("titlemsgTyC")
          : "Importante",
      cssClass: this.globalVars.licenseType === LicenseTypes.Colombia ? "msgtycco" : "",
      message: msg + msgCheckbox,
      buttons: [
        {
          text: this.translate.instant("tCancel"),
          role: "cancel",
        },
        {
          text:
            this.globalVars.licenseType === LicenseTypes.Colombia
              ? this.translate.instant("tAccept")
              : this.translate.instant("CodereCardconfir"),
          handler: buttonHandler,
        },
      ],
    });
    await alert.present();
  }

  showAlertPrivacePolitics(userName: string) {
    const license = this.globalVars.licenseType.toString();
    return new Promise<boolean>((resolve) => {
      this.utilsMsoService.getLastPrivacyPolitics(license).subscribe({
        next: async (data3) => {
          let urlTyc: string = data3.Url;
          let extraUrl = null;
          if (
            this.globalVars.licenseType === LicenseTypes.Colombia ||
            this.globalVars.licenseType === LicenseTypes.ArgentinaCaba
          ) {
            extraUrl = { urlPrivacePolitics: "", urlautotratadatospersonal: "" };
            const responseTyc = await firstValueFrom(this.utilsMsoService.getLastTermsAndConditions());

            urlTyc = responseTyc.Url;
            extraUrl.urlPrivacePolitics = data3.Url;
            if (this.globalVars.licenseType === LicenseTypes.Colombia) {
              const responseautotratadatospersonal = await firstValueFrom(
                this.utilsMsoService.getLastAuthorizationPersonalData(license),
              );
              extraUrl.urlautotratadatospersonal = responseautotratadatospersonal.Url;
            }
          }
          if (this.globalVars.licenseType === LicenseTypes.ArgentinaCaba) {
            this.showAcceptTermsArg(urlTyc, data3.Url, async () => {
              try {
                const request = await Promise.all([
                  firstValueFrom(this.utilsMsoService.updateTermsAndConditions(userName)),
                  firstValueFrom(this.utilsMsoService.updatePrivacePolitics(userName)),
                ]);
                if (request[0].status === "ok" && request[1].status === "ok") {
                  resolve(true);
                } else {
                  this.showAlerErrorUpdate();
                  resolve(false);
                }
              } catch (_) {
                this.showAlerErrorUpdate();
                resolve(false);
              }
            });
          } else {
            this.showAlertAcceptTerms(
              urlTyc,
              () => {
                this.logService.log("update politics:", userName);
                this.utilsMsoService.updatePrivacePolitics(userName).subscribe({
                  next: (data2) => {
                    if (data2.status === "ok") {
                      if (this.globalVars.licenseType === LicenseTypes.Colombia) {
                        this.utilsMsoService.updateAuthorizationPersonalData(userName).subscribe({
                          next: (apdResponse) => {
                            if (apdResponse.status === "ok") {
                              this.utilsMsoService.updateTermsAndConditions(userName).subscribe({
                                next: (data4) => {
                                  if (data4.status === "ok") {
                                    resolve(true);
                                  } else {
                                    this.showAlerErrorUpdate();
                                    resolve(false);
                                  }
                                },
                                error: (_) => {
                                  this.showAlerErrorUpdate();
                                  resolve(false);
                                },
                              });
                            } else {
                              this.showAlerErrorUpdate();
                              resolve(false);
                            }
                          },
                          error: (_) => {
                            this.showAlerErrorUpdate();
                            resolve(false);
                          },
                        });
                      } else {
                        resolve(true);
                      }
                    } else {
                      this.showAlerErrorUpdate();
                      resolve(false);
                    }
                  },
                  error: (_) => {
                    this.showAlerErrorUpdate();
                    resolve(false);
                  },
                });
              },
              false,
              extraUrl,
            );
          }
        },
        error: () => {
          this.showAlerErrorUpdate();
          resolve(false);
        },
      });
    });
  }

  showAlertChangePassword() {
    return new Promise<boolean>((resolve) => {
      if (this.globalVars.licenseType === LicenseTypes.Nacional) {
        this.utils
          .confirmcustom(
            false,
            this.translate.instant("tChangePass"),
            this.translate.instant("changePasstxt"),
            this.translate.instant("tCancel"),
            () => {
              resolve(false);
            },
            this.translate.instant("tChangePass"),
            () => {
              resolve(true);
            },
            false,
            "changePasswordAlert",
          )
          .then((confirmAlertI) => {
            confirmAlertI.present().then(() => {
              const nodeIonAlert = confirmAlertI.querySelector(".alert-wrapper");
              const nodeTitle = nodeIonAlert.querySelector(".alert-title");
              const nodeHeader = nodeIonAlert.querySelector(".alert-head");
              const nodeIcon = document.createElement("ion-icon");
              nodeIcon.setAttribute("name", "lock-closed-outline");
              nodeIcon.setAttribute("role", "img");
              nodeIcon.classList.add("icon", "icon-md", "ion-lock-outline");
              nodeIcon.setAttribute("aria-label", "lock-outline");
              nodeHeader.insertBefore(nodeIcon, nodeTitle);
            });
          });
      } else {
        this.utils.confirm(
          false,
          this.translate.instant("fail_login_title"),
          this.translate.instant("accountBlocked"),
          this.translate.instant("tCancel"),
          () => {
            resolve(false);
          },
          this.translate.instant("retrieveit"),
          () => {
            setTimeout(() => {
              resolve(true);
            }, 300);
          },
        );
      }
    });
  }

  showAlertRecoverPassword() {
    return new Promise<boolean>((resolve) => {
      this.utils.confirm(
        false,
        this.translate.instant("fail_login_title"),
        this.translate.instant("usernameIncorrect"),
        this.translate.instant("tCancel"),
        () => {
          resolve(false);
        },
        "Recupérala",
        () => {
          setTimeout(() => {
            resolve(true);
          }, 300);
        },
      );
    });
  }

  showAlertUserService(errorCode: string, funcAfterAlert: VoidFunction) {
    this.errorService.searchErrorCode("error" + errorCode).subscribe({
      next: (data) => {
        const msgCode: string = data;
        this.utils.alert(true, this.translate.instant("fail_login_title"), msgCode, "OK", funcAfterAlert);
      },
      error: (_) => {
        this.utils.alert(
          true,
          this.translate.instant("fail_login_title"),
          this.translate.instant("m_reg_loginKo"),
          "OK",
          funcAfterAlert,
          "loginko",
        );
      },
    });
  }

  showAlertExitQuick(errorCode: string) {
    const fullTime = errorCode.slice(errorCode.indexOf("ExitQuick") + 16);
    const aTime = fullTime.split(" ");
    const literal = this.translate.instant("exitrapidtxt"); //Activaste la Salida Rápida y no podrás logarte hasta el
    const msg = `${literal} ${aTime[0]} ${this.translate.instant("lasatconexhora")} ${aTime[1]}`;
    this.utils.showMsg(msg);
  }

  showAlertDefaultLogin() {
    return new Promise<boolean>((resolve) => {
      this.utils.alertLoginError(
        true,
        this.translate.instant("fail_login_title"),
        this.translate.instant("m_reg_loginKo"),
        [
          {
            text: "¿Olvidó su contraseña?",
            role: null,
            cssClass: "orange",
            handler: () => {
              resolve(true);
            },
          },
          {
            text: "OK",
            cssClass: "loginkobtn",
            role: null,
            handler: () => {
              resolve(false);
            },
          },
        ],
        false,
        false,
      );
    });
  }

  public showAlertSessionOut() {
    this.utils.showError(this.translate.instant("m_menu_TimeOut"), () => {
      // no hacemos nada para no entrar en bucle
    });
  }

  public async showAlertRuamDown() {
    const msg = this.translate.instant("ruamdown");
    const header = this.translate.instant("ruamDowntitle");
    const alertError = await this.alertCtr.create({
      header: header,
      backdropDismiss: true,
      cssClass: "alert-ruam-down",
      message: msg,
    });
    return new Promise<void>((resolve) => {
      alertError.present().then(() => {
        const header = alertError.querySelector(".alert-head");
        const icon = document.createElement("i");
        icon.classList.add("codere-icon", "icon-close");
        icon.addEventListener("click", () => {
          alertError.dismiss();
        });
        header.appendChild(icon);
      });
      alertError.onDidDismiss().then(() => {
        resolve();
      });
    });
  }

  showAlertGraceTime(literalMsg: string, handleBntOk: VoidFunction) {
    this.utils.alert(false, "¡Coderista!", this.translate.instant(literalMsg), "OK", () => handleBntOk());
  }
}
